import React from "react";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";

const AssetTableBody = ({
  tableData = [],
  loading = false,
  columnSearch = {},
  renderHighlightedText = (t) => t,
}) => {
  return (
    <tbody>
      {loading ? (
        [...Array(7)].map((_, i) => (
          <tr key={i}>
            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={100} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={120} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={100} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={120} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={130} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={100} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="text" width={100} height={20} />
            </td>

            <td className="px-[25px] py-2">
              <Skeleton variant="rectangular" width="100%" height={20} />
            </td>
            <td className="px-[25px] py-2">
              <Skeleton variant="rectangular" width="100%" height={20} />
            </td>
          </tr>
        ))
      ) : tableData.length === 0 ? (
        <tr>
          <td colSpan="8" className="text-center py-10 text-gray-500">
            No Data Available
          </td>
        </tr>
      ) : (
        tableData.map((row, idx) => {
          const {
            domain,
            sub_domain,
            data_product_name,
            region,
            data_asset_name,
            frequency,
            execution_date,
            total_dq_score,
            uniqueness_qs,
            validity_qs,
            freshness_qs,
            consistency_qs,
            completeness_qs,
            accuracy_qs,
            status
          } = row;

          let textColor = "black";
          let tooltipBgColor = "#fff";
          if (total_dq_score != null) {
            if (total_dq_score < 90) {
              textColor = "#FB0301";
              tooltipBgColor = "#f03e3eff";
            } else if (total_dq_score >= 90 && total_dq_score < 95) {
              textColor = "#FFC100";
              tooltipBgColor = "#FFC100";
            } else if (total_dq_score >= 95) {
              textColor = "#009F52";
              tooltipBgColor = "#009F52";
            }
          }

          const dateStr = execution_date
            ? new Date(execution_date).toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "-";

          return (
            <tr
              key={idx}
              className={`transition-colors cursor-pointer ${
                idx % 2 === 0 ? "bg-white" : "#eee"
              } hover:!bg-[#E4F8F1]`}
            >
              <td className="px-[25px] text-[10px]  text-[#404040] py-2">
                <Tooltip
                  title={domain || "-"}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ece5e5ff",
                        color: "black",
                        fontSize: "10px",
                        fontFamily: "Poppins",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        maxWidth: "400px",
                        whiteSpace: "pre-wrap",
                      },
                    },
                  }}
                >
                  <span className="block w-[100px] text-[10px]  text-[#404040] truncate">
                    {domain ? (
                      renderHighlightedText(
                        domain.length > 10 ? domain.slice(0, 10) + "..." : domain,
                        columnSearch.domain_name
                      )
                    ) : (
                      "-"
                    )}
                  </span>
                </Tooltip>
              </td>

              <td className="px-[25px] py-2 text-[10px]  text-[#404040]">
                <Tooltip
                  title={sub_domain || ""}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ece5e5ff",
                        color: "black",
                        fontSize: "10px",
                        fontFamily: "Poppins",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        maxWidth: "400px",
                        whiteSpace: "pre-wrap",
                      },
                    },
                  }}
                >
                  <span className="truncate block text-[10px]  text-[#404040] w-[120px]">
                    {sub_domain
                      ? renderHighlightedText(
                          sub_domain.length > 15 ? sub_domain.slice(0, 15) + "..." : sub_domain,
                          columnSearch.sub_domain_name
                        )
                      : "null"}
                  </span>
                </Tooltip>
              </td>

              <td className="px-[25px] py-2 text-[10px]  text-[#404040]">
                <Tooltip
                  title={data_product_name || ""}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ece5e5ff",
                        color: "black",
                        fontSize: "10px",
                        fontFamily: "Poppins",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        maxWidth: "400px",
                        whiteSpace: "pre-wrap",
                      },
                    },
                  }}
                >
                  <span className="truncate block w-[100px] text-[10px]  text-[#404040]">
                    {data_product_name
                      ? renderHighlightedText(
                          data_product_name.length > 15
                            ? data_product_name.slice(0, 15) + "..."
                            : data_product_name,
                          columnSearch.product_name
                        )
                      : "null"}
                  </span>
                </Tooltip>
              </td>

              <td className="px-[25px] py-2 text-[10px]  text-[#404040]">
                <Tooltip
                  title={region || ""}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ece5e5ff",
                        color: "black",
                        fontSize: "10px",
                        fontFamily: "Poppins",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        maxWidth: "400px",
                        whiteSpace: "pre-wrap",
                      },
                    },
                  }}
                >
                  <span className="truncate block text-[10px]  text-[#404040] w-[120px]">
                    {region ? renderHighlightedText(region, columnSearch.region) : "null"}
                  </span>
                </Tooltip>
              </td>

              <td className="px-[25px] text-[10px]  text-[#404040] py-2">
                <Tooltip
                  title={
                    data_asset_name ? (data_asset_name.includes("|") ? data_asset_name.split("|")[1] : data_asset_name) : ""
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ece5e5ff",
                        color: "black",
                        fontSize: "10px",
                        fontFamily: "Poppins",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        maxWidth: "400px",
                        whiteSpace: "pre-wrap",
                      },
                    },
                  }}
                >
                  <span className="truncate text-[10px]  text-[#404040] block w-[130px]">
                    {data_asset_name ? (
                      renderHighlightedText(
                        data_asset_name.includes("|") ? data_asset_name.split("|")[1] : data_asset_name,
                        columnSearch.data_asset_name
                      )
                    ) : (
                      "null"
                    )}
                  </span>
                </Tooltip>
              </td>

              <td className="px-[25px] text-[10px]  text-[#404040] py-2">
                <span className="w-[100px] text-[10px]  text-[#404040]">
                  {frequency ? renderHighlightedText(frequency, columnSearch.frequency) : "null"}
                </span>
              </td>

              <td className="px-[25px] py-2 w-[100px] text-[10px] text-[#404040]">
                {dateStr ? (
                  renderHighlightedText(
                    new Date(dateStr).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }),
                    columnSearch.execution_date
                  )
                ) : (
                  "null"
                )}
              </td>

              <td className="px-[25px] py-2 text-[10px]  text-[#404040] font-semibold align-top">
                <div className="flex flex-col">
                  <Tooltip
                    title={
                      <div>
                        <p>Uniqueness: {uniqueness_qs ?? "N/A"}</p>
                        <p>Validity: {validity_qs ?? "N/A"}</p>
                        <p>Freshness: {freshness_qs ?? "N/A"}</p>
                        <p>Consistency: {consistency_qs ?? "N/A"}</p>
                        <p>Completeness: {completeness_qs ?? "N/A"}</p>
                        <p>Accuracy: {accuracy_qs ?? "N/A"}</p>
                      </div>
                    }
                    componentsProps={{
                      tooltip: {
                        sx: {
                          padding: "12px 16px",
                          minHeight: "60px",
                          fontSize: "0.9rem",
                          lineHeight: "1.6",
                          backgroundColor:
                            total_dq_score < 90 ? "#f03e3eff" : total_dq_score >= 90 && total_dq_score < 95 ? "#FFC100" : "#009F52",
                        },
                      },
                    }}
                  >
                    <span className="text-left block">
                      {renderHighlightedText(total_dq_score?.toString() ?? "NA", columnSearch.total_dq_score)}
                    </span>
                  </Tooltip>

                  <div className="w-full bg-gray-200 rounded-full h-[8px] mt-1 relative">
                    <div
                      className="h-[8px] rounded-full transition-all duration-300"
                      style={{
                        width: `${parseFloat(total_dq_score) || 0}%`,
                        backgroundColor: total_dq_score >= 95 ? "#009F52" : total_dq_score >= 90 ? "#FFC100" : "#FB0301",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                  </div>
                </div>
              </td>

               <td className="px-[25px] py-2 text-[10px]  text-[#404040]">
                <Tooltip
                  title={status || ""}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ece5e5ff",
                        color: "black",
                        fontSize: "10px",
                        fontFamily: "Poppins",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        maxWidth: "400px",
                        whiteSpace: "pre-wrap",
                      },
                    },
                  }}
                >
                  <span className={`truncate block text-[10px] ${
                  status === "pass"
                    ? "text-green-700"
                    : status === "fail"
                    ? "text-red-700"
                    : "text-[#404040]"
                } w-[120px`}>
                    {status
                      ? renderHighlightedText(
                          status?.length > 15
                        ? status.slice(0, 15).charAt(0).toUpperCase() + status.slice(1, 15) + "..."
                        : status?.charAt(0).toUpperCase() + status?.slice(1),
                          columnSearch.status
                        )
                      : "null"}
                  </span>
                </Tooltip>
              </td>
              
            </tr>
          );
        })
      )}
    </tbody>
  );
};

export default AssetTableBody;
